import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

interface Lista<T> { lista: T[]; csonkolva: boolean; }
interface LejartSor {
  azonosito: string; nev: string | null; jogviszony_vege: string; megorzes_vege: string; lejart_ota_nap: number;
  osztaly?: string | null; felhasznalonev?: string | null; digitalis_bizonyitvany?: number; nyolcvan_eves_vege?: string;
  gyermekek?: number; munkakor?: string | null;
}
interface BizonytalanSor { azonosito: string; nev: string | null; gyermekek?: number; ok?: string; }
interface Kimutatas {
  intezmeny: string; nap: string; ma: string; lista_max: number; figyelmeztetes: string;
  evek: { tanulo: number; szulo: number; alkalmazott: number; bizonyitvany: number };
  jogszabaly: { forras: string; bekezdes_10: string; bekezdes_10a: string };
  tanulok: { ev: number; darab: any; lejart: Lista<LejartSor>; nyolcvan_eves: Lista<LejartSor>; nem_megallapithato: Lista<BizonytalanSor> };
  szulok: { ev: number; darab: any; lejart: Lista<LejartSor>; nem_megallapithato: Lista<BizonytalanSor> };
  alkalmazottak: { ev: number; darab: any; megjegyzes: string; lejart: Lista<LejartSor> };
}
type Ful = 'tanulok' | 'nyolcvan' | 'szulok' | 'alkalmazottak' | 'bizonytalan';

/**
 *  Admin → Adatmegőrzés (backend/megorzes.js), Nkt. 41. § (10)–(10a).
 *
 *  CSAK KIMUTATÁS: kiknek az adatai lépték túl a törvényi kezelési időt – tanulók (utolsó jogviszony
 *  vége + 10 év), szülők (ha minden gyermekük lejárt), alkalmazottak (munkaviszony vége + 5 év) –, és
 *  külön, akiknek a belépési és digitális bizonyítvány-adatai a 80 éves megőrzés alá esnek. Törölni,
 *  anonimizálni innen nem lehet: az külön döntés és külön művelet.
 */
@Component({
  selector: 'app-admin-adatmegorzes',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './adatmegorzes.component.html',
  styleUrls: ['./adatmegorzes.component.scss'],
})
export class AdminAdatmegorzesComponent implements OnInit {
  intezmeny = '';
  hiba = '';
  fut = false;
  k: Kimutatas | null = null;
  nap = '';
  ful: Ful = 'tanulok';
  kereses = '';

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.intezmeny = String((await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.betolt();
  }

  async betolt() {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.fut = true; this.hiba = '';
    try {
      let p = new HttpParams();
      if (/^\d{4}-\d{2}-\d{2}$/.test(this.nap)) p = p.set('nap', this.nap);
      this.k = await firstValueFrom(this.http.get<Kimutatas>(`/api/megorzes/kimutatas/${this.intezmeny}`, { params: p }));
      if (!this.nap) this.nap = this.k?.nap || '';
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A kimutatás nem tölthető be.';
    } finally {
      this.fut = false;
    }
  }

  maiNap() { this.nap = ''; this.betolt(); }
  get masikNap() { return !!this.k && this.k.nap !== this.k.ma; }

  get bizonytalanDb() {
    return (this.k?.tanulok.darab?.nem_megallapithato || 0) + (this.k?.szulok.darab?.nem_megallapithato || 0);
  }

  private szur<T extends { nev: string | null; azonosito: string }>(l: T[] | undefined): T[] {
    const s = this.kereses.trim().toLowerCase();
    if (!l) return [];
    return s ? l.filter((x) => (x.nev || '').toLowerCase().includes(s) || x.azonosito.toLowerCase().includes(s)) : l;
  }

  get aktualis(): LejartSor[] {
    if (!this.k) return [];
    if (this.ful === 'tanulok') return this.szur(this.k.tanulok.lejart.lista);
    if (this.ful === 'nyolcvan') return this.szur(this.k.tanulok.nyolcvan_eves.lista);
    if (this.ful === 'szulok') return this.szur(this.k.szulok.lejart.lista);
    if (this.ful === 'alkalmazottak') return this.szur(this.k.alkalmazottak.lejart.lista);
    return [];
  }
  get bizonytalanTanulok() { return this.szur(this.k?.tanulok.nem_megallapithato.lista); }
  get bizonytalanSzulok() { return this.szur(this.k?.szulok.nem_megallapithato.lista); }

  get csonkolva() {
    if (!this.k) return false;
    return ({ tanulok: this.k.tanulok.lejart, nyolcvan: this.k.tanulok.nyolcvan_eves, szulok: this.k.szulok.lejart,
      alkalmazottak: this.k.alkalmazottak.lejart, bizonytalan: this.k.tanulok.nem_megallapithato } as Record<Ful, Lista<any>>)[this.ful]?.csonkolva
      || (this.ful === 'bizonytalan' && this.k.szulok.nem_megallapithato.csonkolva);
  }

  /** A lejárat óta eltelt idő emberi alakban. */
  ota(nap: number) {
    if (nap < 31) return `${nap} napja`;
    if (nap < 365) return `${Math.floor(nap / 30.44)} hónapja`;
    const ev = Math.floor(nap / 365.25);
    const ho = Math.floor((nap - ev * 365.25) / 30.44);
    return ho ? `${ev} év ${ho} hónapja` : `${ev} éve`;
  }

  /** CSV a megjelenített listából – a gépen marad, a kiszolgáló nem kapja vissza. */
  csv() {
    const l = this.aktualis;
    if (!l.length) return;
    const fej = ['Azonosító', 'Név', 'Jogviszony vége', 'Megőrzés vége', 'Lejárt óta (nap)'];
    const extra = this.ful === 'nyolcvan' ? ['Digitális bizonyítvány', '80 éves megőrzés vége']
      : this.ful === 'szulok' ? ['Gyermekek'] : this.ful === 'alkalmazottak' ? ['Munkakör'] : ['Osztály'];
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const sorok = l.map((s) => [s.azonosito, s.nev, s.jogviszony_vege, s.megorzes_vege, s.lejart_ota_nap,
      ...(this.ful === 'nyolcvan' ? [s.digitalis_bizonyitvany, s.nyolcvan_eves_vege]
        : this.ful === 'szulok' ? [s.gyermekek] : this.ful === 'alkalmazottak' ? [s.munkakor] : [s.osztaly])]);
    const tartalom = '﻿' + [[...fej, ...extra], ...sorok].map((r) => r.map(esc).join(';')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([tartalom], { type: 'text/csv;charset=utf-8' }));
    a.download = `adatmegorzes-${this.ful}-${this.k?.nap || ''}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }
}
